import React, { useEffect, useState } from 'react';
import { MdComputer, MdLanguage, MdViewModule, MdVisibility } from 'react-icons/md';
import KPICard from './KPICard';
import {
  getActiveMeasurementMetrics,
  ActiveMeasurementMetrics
} from '../services/measurementService';
import './ActiveMeasurementMetricsPanel.css';

interface ActiveMeasurementMetricsPanelProps {
  measurementId: string;
}

const formatPortion = (value: number): string =>
  `${Number.isFinite(value) ? value.toFixed(1) : '—'}%`;

/** Same compact style as MeasurementHeader impressions KPI. */
const formatImpressionsCount = (value: number): string => {
  if (!Number.isFinite(value)) return '—';
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toString();
};

const ActiveMeasurementMetricsPanel: React.FC<ActiveMeasurementMetricsPanelProps> = ({
  measurementId
}) => {
  const [metrics, setMetrics] = useState<ActiveMeasurementMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setMetrics(null);

    getActiveMeasurementMetrics(measurementId)
      .then((data) => {
        if (!cancelled) setMetrics(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load metrics');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [measurementId]);

  return (
    <div className="active-measurement-metrics details-section">
      <h2>Collection progress</h2>
      <p className="active-measurement-metrics-intro">
        Impression collection progress and quality KPIs.
      </p>
      {loading && <div className="loading-state">Loading collection metrics…</div>}
      {error && <div className="script-inline-error">{error}</div>}
      {!loading && !error && metrics && (
        <div className="active-measurement-metrics-kpis">
          <KPICard
            icon={MdComputer}
            headline="Device"
            value={formatPortion(metrics.devicePercentage)}
          />
          <KPICard
            icon={MdLanguage}
            headline="Site / app"
            value={formatPortion(metrics.siteOrAppPercent)}
          />
          <KPICard
            icon={MdViewModule}
            headline="Ad unit"
            value={formatPortion(metrics.adUnitPercent)}
          />
          <KPICard
            icon={MdVisibility}
            headline="IMPRESSIONS"
            value={formatImpressionsCount(metrics.impressions)}
          />
        </div>
      )}
    </div>
  );
};

export default ActiveMeasurementMetricsPanel;
